import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Gift, ArrowRight, Loader2, AlertCircle, History, Package, Clock, CheckCircle, Truck, XCircle, ShieldOff, BadgeCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function Home() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("rewards");
  
  // User data from UID lookup
  const [uid, setUid] = useState("");
  const [userData, setUserData] = useState(null);
  const [userNotFound, setUserNotFound] = useState(false);
  
  // Points history
  const [pointsHistory, setPointsHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Read UID from URL and fetch user data on mount
  useEffect(() => {
    const urlUid = searchParams.get("uid");
    if (urlUid) {
      setUid(urlUid);
      fetchUserByUID(urlUid);
    } else {
      setInitialLoading(false);
    }
  }, [searchParams]);

  // Fetch user data by UID from Taj Cement API
  const fetchUserByUID = async (userUid) => {
    setInitialLoading(true);
    setUserNotFound(false);
    
    try {
      const response = await axios.post(`${API}/taj/user-by-uid`, { uid: userUid });
      if (response.data.found) {
        setUserData(response.data);
        toast.success(`Welcome, ${response.data.name}!`);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      setUserNotFound(true);
      toast.error("User not found. Please check your registration.");
    } finally {
      setInitialLoading(false);
    }
  };

  // Fetch points history when history tab is selected
  const fetchPointsHistory = async () => {
    if (!uid || pointsHistory) return;
    
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/rewards/history/${uid}`);
      setPointsHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch points history:", error);
      setPointsHistory({ summary: { total_orders: 0, total_points_redeemed: 0 }, transactions: [] });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch history when tab changes to history
  useEffect(() => {
    if (activeTab === "history" && !pointsHistory) {
      fetchPointsHistory();
    }
  }, [activeTab]);

  const isCompanyVerified = userData?.company_verified?.toLowerCase() === "yes";

  const handleCreateRewardsSession = async (e) => {
    e.preventDefault();
    
    if (!userData) {
      toast.error("User data not loaded");
      return;
    }

    if (!isCompanyVerified) {
      toast.error("Your account is not verified. Please contact Taj Cement support.");
      return;
    }
    
    if (!userData.available_points || userData.available_points <= 0) {
      toast.error("No points available to redeem");
      return;
    }

    setLoading(true);
    
    // Build query params for GET-based session creation (avoids CORS preflight in WebViews)
    const params = new URLSearchParams({
      mobile: userData.mobile,
      available_points: userData.available_points,
      taj_user_id: uid,
      user_type: userData.user_type || "",
      name: userData.name || ""
    });

    try {
      // Strategy 1: GET request (no CORS preflight needed)
      const response = await fetch(`${API}/rewards/session/init?${params.toString()}`, {
        method: "GET",
        mode: "cors"
      });
      
      if (!response.ok) {
        const errText = await response.text().catch(() => "");
        throw new Error(`HTTP ${response.status}: ${errText}`);
      }
      
      const data = await response.json();
      toast.success("Rewards Session Created");
      const url = new URL(data.web_url);
      navigate(`${url.pathname}${url.search}&uid=${uid}`);
    } catch (fetchError) {
      console.error("GET session failed:", fetchError?.message || fetchError);
      
      // Strategy 2: Direct redirect (no JS fetch at all - ultimate fallback)
      try {
        console.log("Falling back to direct redirect...");
        const redirectUrl = `${API}/rewards/session/redirect?${params.toString()}`;
        window.location.href = redirectUrl;
        return; // Navigation will happen via redirect
      } catch (redirectError) {
        console.error("Redirect fallback also failed:", redirectError);
        toast.error(`Connection failed. Please check your network and try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading user data...</p>
        </div>
      </div>
    );
  }

  // No UID provided
  if (!uid) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-primary text-primary-foreground py-6 px-4">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_a79b9f2f-e1ce-41bf-8c44-d5f2616bf672/artifacts/mx91th95_TAJ.png" 
              alt="Taj Cement" 
              className="h-10 w-auto brightness-0 invert"
            />
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>CHANNEL LOYALTY</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-16">
          <Card className="rounded-sm border">
            <CardContent className="pt-6 text-center space-y-4">
              <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto" />
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Access Required</h2>
              <p className="text-muted-foreground">
                Please access this portal through the Taj Cement app with your registered account.
              </p>
            </CardContent>
          </Card>
        </main>
        <footer className="border-t mt-12 py-6 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-3">
            <span className="text-sm text-muted-foreground">Powered by</span>
            <img 
              src={require("@/assets/tagnpay-logo.png")} 
              alt="Tag'N'Pay" 
              className="h-8 w-auto"
            />
          </div>
        </footer>
      </div>
    );
  }

  // User not found
  if (userNotFound) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-primary text-primary-foreground py-6 px-4">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_a79b9f2f-e1ce-41bf-8c44-d5f2616bf672/artifacts/mx91th95_TAJ.png" 
              alt="Taj Cement" 
              className="h-10 w-auto brightness-0 invert"
            />
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>CHANNEL LOYALTY</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-16">
          <Card className="rounded-sm border">
            <CardContent className="pt-6 text-center space-y-4">
              <AlertCircle className="w-16 h-16 text-destructive mx-auto" />
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>User Not Found</h2>
              <p className="text-muted-foreground">
                Your registration ID was not found in the system. Please contact support.
              </p>
              <p className="text-sm text-muted-foreground">UID: {uid}</p>
            </CardContent>
          </Card>
        </main>
        <footer className="border-t mt-12 py-6 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-3">
            <span className="text-sm text-muted-foreground">Powered by</span>
            <img 
              src={require("@/assets/tagnpay-logo.png")} 
              alt="Tag'N'Pay" 
              className="h-8 w-auto"
            />
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <img 
            src="https://customer-assets-agu9un31.emergentagent.net/job_a79b9f2f-e1ce-41bf-8c44-d5f2616bf672/artifacts/mx91th95_TAJ.png" 
            alt="Taj Cement" 
            className="h-10 w-auto brightness-0 invert"
          />
          <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>CHANNEL LOYALTY</h1>
        </div>
      </header>

      {/* User Info Banner */}
      <section className="bg-gradient-to-b from-primary/5 to-background py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-sm border p-6 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Welcome</p>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{userData?.name}</h2>
                <p className="text-sm text-muted-foreground capitalize mt-1">
                  {userData?.user_type?.replace("_", " ")} &bull; {userData?.mobile}
                </p>
              </div>
              <div className="flex items-center gap-4">
                {isCompanyVerified ? (
                  <span className="flex items-center gap-1 text-sm text-green-600 bg-green-100 px-3 py-1.5 rounded" data-testid="verified-badge">
                    <BadgeCheck className="w-4 h-4" />
                    Verified
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-sm text-red-600 bg-red-100 px-3 py-1.5 rounded" data-testid="not-verified-badge">
                    <ShieldOff className="w-4 h-4" />
                    Not Verified
                  </span>
                )}
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Available Points</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: '#F2B721' }} data-testid="home-points-balance">
                    {userData?.available_points?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Tab Buttons */}
        <div className="flex gap-2 mb-8">
          <Button
            variant={activeTab === "rewards" ? "default" : "outline"}
            className="flex-1 h-12 rounded-none uppercase tracking-wider text-xs sm:text-sm"
            onClick={() => setActiveTab("rewards")}
            data-testid="rewards-tab-btn"
          >
            <Gift className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Rewards </span>Store
          </Button>
          <Button
            variant={activeTab === "history" ? "default" : "outline"}
            className="flex-1 h-12 rounded-none uppercase tracking-wider text-xs sm:text-sm"
            onClick={() => setActiveTab("history")}
            data-testid="history-tab-btn"
          >
            <History className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Points </span>History
          </Button>
        </div>

        {/* Rewards Section */}
        {activeTab === "rewards" && (
          <Card className="rounded-sm border fade-in" data-testid="rewards-form-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Gift className="w-6 h-6" />
                Rewards Store
              </CardTitle>
              <CardDescription>
                Redeem your loyalty points for exciting rewards.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!isCompanyVerified ? (
                /* Company Not Verified - Rewards Disabled */
                <div className="bg-red-50 border border-red-200 rounded-sm p-6 text-center space-y-3" data-testid="rewards-disabled-banner">
                  <ShieldOff className="w-12 h-12 text-red-400 mx-auto" />
                  <h3 className="font-semibold text-red-800" style={{ fontFamily: 'Manrope' }}>
                    Account Not Verified
                  </h3>
                  <p className="text-sm text-red-600">
                    Your account verification is pending. The Gift Rewards section will be enabled once your account is verified by Taj Cement.
                  </p>
                  <p className="text-xs text-red-500">
                    Please contact your Taj Cement representative for verification.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Points Summary */}
                  <div className="bg-muted rounded-sm p-6 text-center">
                    <p className="text-sm text-muted-foreground uppercase tracking-wider">Your Points Balance</p>
                    <p className="text-4xl font-bold mt-2" style={{ fontFamily: 'Manrope', color: '#F2B721' }} data-testid="rewards-points-display">
                      {userData?.available_points?.toLocaleString() || 0}
                    </p>
                    <div className="flex justify-center gap-6 mt-4 text-sm text-muted-foreground">
                      <span>Total Earned: {userData?.total_earned?.toLocaleString() || 0}</span>
                      <span>Total Redeemed: {userData?.total_redeemed?.toLocaleString() || 0}</span>
                    </div>
                  </div>

                  {userData?.available_points > 0 ? (
                    <Button 
                      onClick={handleCreateRewardsSession}
                      className="w-full h-14 rounded-none uppercase tracking-wider btn-gold"
                      disabled={loading}
                      data-testid="rewards-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Gift className="w-5 h-5 mr-2" />
                      )}
                      Browse Rewards Store
                    </Button>
                  ) : (
                    <div className="text-center text-muted-foreground py-4">
                      <p>You don't have any points to redeem yet.</p>
                      <p className="text-sm mt-1">Keep purchasing to earn more points!</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Points History Section */}
        {activeTab === "history" && (
          <Card className="rounded-sm border fade-in" data-testid="history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <History className="w-6 h-6" />
                Points History
              </CardTitle>
              <CardDescription>
                View your redemption history and point transactions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : pointsHistory ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-muted rounded-sm p-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase">Total Orders</p>
                      <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Manrope' }}>
                        {pointsHistory.summary?.total_orders || 0}
                      </p>
                    </div>
                    <div className="bg-muted rounded-sm p-4 text-center">
                      <p className="text-xs text-muted-foreground uppercase">Points Redeemed</p>
                      <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Manrope', color: '#F2B721' }}>
                        {pointsHistory.summary?.total_points_redeemed?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-sm p-4 text-center">
                      <p className="text-xs text-green-700 uppercase">Delivered</p>
                      <p className="text-2xl font-bold mt-1 text-green-700" style={{ fontFamily: 'Manrope' }}>
                        {pointsHistory.summary?.delivered_orders || 0}
                      </p>
                    </div>
                    <div className="bg-yellow-50 rounded-sm p-4 text-center">
                      <p className="text-xs text-yellow-700 uppercase">Pending</p>
                      <p className="text-2xl font-bold mt-1 text-yellow-700" style={{ fontFamily: 'Manrope' }}>
                        {pointsHistory.summary?.pending_orders || 0}
                      </p>
                    </div>
                  </div>

                  {/* Transaction List */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                      Recent Transactions
                    </h4>
                    
                    {pointsHistory.transactions?.length > 0 ? (
                      <div className="space-y-2">
                        {pointsHistory.transactions.map((tx, idx) => (
                          <div 
                            key={tx.order_id || idx} 
                            className="bg-white border rounded-sm p-4 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-sm ${
                                tx.fulfillment_status === 'delivered' ? 'bg-green-100' :
                                tx.fulfillment_status === 'shipped' ? 'bg-blue-100' :
                                tx.fulfillment_status === 'processing' ? 'bg-yellow-100' :
                                tx.fulfillment_status === 'cancelled' ? 'bg-red-100' :
                                'bg-gray-100'
                              }`}>
                                {tx.fulfillment_status === 'delivered' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                                 tx.fulfillment_status === 'shipped' ? <Truck className="w-5 h-5 text-blue-600" /> :
                                 tx.fulfillment_status === 'processing' ? <Clock className="w-5 h-5 text-yellow-600" /> :
                                 tx.fulfillment_status === 'cancelled' ? <XCircle className="w-5 h-5 text-red-600" /> :
                                 <Package className="w-5 h-5 text-gray-600" />}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{tx.item_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {tx.order_id} &bull; {new Date(tx.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold" style={{ color: '#F2B721' }}>
                                -{tx.points_redeemed?.toLocaleString()} pts
                              </p>
                              <p className={`text-xs capitalize ${
                                tx.fulfillment_status === 'delivered' ? 'text-green-600' :
                                tx.fulfillment_status === 'shipped' ? 'text-blue-600' :
                                tx.fulfillment_status === 'cancelled' ? 'text-red-600' :
                                'text-yellow-600'
                              }`}>
                                {tx.fulfillment_status || 'pending'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No transactions yet</p>
                        <p className="text-sm mt-1">Your redemption history will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Unable to load history</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <div className="mt-8">
          <Card className="rounded-sm border">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-sm" style={{ background: 'linear-gradient(135deg, #F2B721 0%, #F9D96A 50%, #F2B721 100%)' }}>
                  <Gift className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1" style={{ fontFamily: 'Manrope' }}>Rewards Flow</h3>
                  <p className="text-sm text-muted-foreground">
                    Browse catalog &rarr; Select gift &rarr; Confirm address &rarr; Place order &rarr; Delivery
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12 py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-3">
          <span className="text-sm text-muted-foreground">Powered by</span>
          <img 
            src={require("@/assets/tagnpay-logo.png")} 
            alt="Tag'N'Pay" 
            className="h-8 w-auto"
          />
        </div>
      </footer>
    </div>
  );
}
